/**
 * UndoButton - NPC 戦のみ表示される「直前の 1 手（プレイヤー + AI 応答）を巻き戻す」ボタン。
 *
 * 現在のゲームデザインでは Undo 広告導線は無効化されており、GameScreen から
 * `visible=false` で扱う。
 *
 * 表示状態（UI ステート）:
 *   1. isUnlocking             → ロード表示・無効化
 *   2. !isUnlocked             → UNLOCK ラベル（広告視聴を促す）
 *   3. isUnlocked & 使い切り   → USED ラベル・無効化
 *   4. isUnlocked & canUndo    → Undo ラベル・アクティブ
 *   5. isUnlocked & !canUndo   → Undo ラベル・無効化（AI 思考中/スナップショット無し）
 *
 * マルチプレイ対応:
 *   `visible = false` の場合、コンポーネント自体を描画しない。GameScreen 側で
 *   `mode === 'npc'` の判定を行い、マルチ時は hidden にする（レイアウト枠ごと消す）。
 *
 * 参考ルール:
 *   - `.claude/rules/no-hardcode.md`
 */
import {
  UNDO_BUTTON_LABEL,
  UNDO_UNLOCK_LABEL,
  UNDO_UNLOCKING_LABEL,
  UNDO_USED_LABEL,
} from '../config/undo'

interface Props {
  /** 盤面ロールバックが実行可能か（useGame 側で判定した結果）。 */
  canUndo: boolean
  /** 広告視聴済みで解放されているか。 */
  isUnlocked: boolean
  /** 残使用回数。0 で使い切り表示に切り替わる。 */
  remainingUses: number
  /** 広告スタブ再生中か。 */
  isUnlocking: boolean
  /** 広告視聴で解放する要求。 */
  onUnlock: () => void
  /** Undo 実行要求。 */
  onUndo: () => void
  /** 表示するか。マルチ時は false で非表示。 */
  visible: boolean
}

export function UndoButton({
  canUndo,
  isUnlocked,
  remainingUses,
  isUnlocking,
  onUnlock,
  onUndo,
  visible,
}: Props) {
  if (!visible) return null

  // 状態を 1 つの軸に畳み込む（視認性のため if-else ではなく分類関数）。
  const isUsedUp = isUnlocked && remainingUses <= 0
  const isActive = isUnlocked && !isUsedUp && canUndo

  const label = (() => {
    if (isUnlocking) return UNDO_UNLOCKING_LABEL
    if (!isUnlocked) return UNDO_UNLOCK_LABEL
    if (isUsedUp) return UNDO_USED_LABEL
    return UNDO_BUTTON_LABEL
  })()

  const disabled = (() => {
    if (isUnlocking) return true
    if (!isUnlocked) return false // 解放ボタンは押せる
    if (isUsedUp) return true
    return !canUndo // 解放済みで残ありだが AI 思考中 / snapshot 無し
  })()

  const variantClass = (() => {
    if (!isUnlocked) return 'undo-button--locked'
    if (isUsedUp) return 'undo-button--used'
    if (isActive) return 'undo-button--active'
    return 'undo-button--disabled'
  })()

  const handleClick = () => {
    if (disabled) return
    if (!isUnlocked) {
      onUnlock()
      return
    }
    onUndo()
  }

  return (
    <button
      type="button"
      className={`undo-button ${variantClass}`}
      onClick={handleClick}
      disabled={disabled}
      aria-label={label}
    >
      <span className="undo-button__icon" aria-hidden="true">↺</span>
      <span className="undo-button__label">{label}</span>
    </button>
  )
}
